'use client'

import * as React from 'react'
import * as SliderPrimitive from '@radix-ui/react-slider'
import { cn } from '../utils/cn'
import { cva } from 'class-variance-authority'
import { IconInfinity } from '@tabler/icons-react'

const variants = cva('relative flex touch-none select-none items-center', {
  variants: {
    orientation: {
      vertical: 'flex flex-col w-2 h-40',
      horizontal: 'w-full',
    },
  },
  defaultVariants: {
    orientation: 'horizontal',
  },
})

/**
 * An input where the user selects a value from within a given range.
 */
const Slider = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root> & {
    thumbIcon?: React.ReactNode
    thumbClassName?: string
    rangeClassName?: string
    trackClassName?: string
    withThumbValue?: boolean
    thumbValue?: number
    withMaxValue?: boolean
    isUnlimited?: boolean
    withThumb?: boolean
  }
>(
  (
    {
      className,
      orientation = 'horizontal',
      thumbIcon,
      thumbClassName,
      rangeClassName,
      trackClassName,
      withThumbValue = false,
      thumbValue,
      withMaxValue = false,
      isUnlimited = false,
      withThumb = true,
      ...props
    },
    ref,
  ) => {
    return (
      <SliderPrimitive.Root
        ref={ref}
        orientation={orientation}
        className={cn(
          variants({
            orientation,
            className,
          }),
        )}
        {...props}
      >
        <SliderPrimitive.Track
          className={cn(
            'relative h-2 w-full grow overflow-hidden rounded-full bg-secondary',
            trackClassName,
          )}
        >
          <SliderPrimitive.Range
            className={cn(
              'absolute h-full bg-primary',
              {
                'w-full': orientation === 'vertical',
              },
              rangeClassName,
            )}
          />
          {withMaxValue && props.max && (
            <span className='absolute right-0 top-0 z-10 text-slate-300 font-medium mt-4 text-xs'>
              {isUnlimited ? <IconInfinity className='size-4' /> : props.max}
            </span>
          )}
        </SliderPrimitive.Track>
        {withThumb && thumbIcon ? (
          <SliderPrimitive.Thumb
            asChild
            className='shadow-lg border block h-5 w-5 rounded-full border-primary bg-background ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50'
          >
            {thumbIcon}
          </SliderPrimitive.Thumb>
        ) : withThumb ? (
          <SliderPrimitive.Thumb
            className={cn(
              'relative block h-5 w-5 rounded-full border-2 border-primary bg-background ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
              thumbClassName,
            )}
          >
            {withThumbValue && (
              <div className='mt-5 text-center flex absolute left-0 right-0 items-center justify-center'>
                <span className='text-xs text-violet-300 font-medium'>
                  {props.value?.[0]}
                </span>
              </div>
            )}
          </SliderPrimitive.Thumb>
        ) : null}
      </SliderPrimitive.Root>
    )
  },
)
Slider.displayName = SliderPrimitive.Root.displayName

export { Slider }
